import React, { useState } from 'react';
import {
  Puzzle,
  Image as ImageIcon,
  Music,
  Video,
  Play,
  Pause,
  Scale,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import InfiniteScrollContainer from '../../UI/InfiniteScrollContainer';
import ConfirmationModal from '../../UI/ConfirmationModal';

/**
 * MediaRenderer - Base component for rendering media items with drag/drop,
 * infinite scroll, and video preview capabilities
 */
const MediaRenderer = ({
  apiData,
  dataFetcher,
  pageSize = 20,
  gridCols = 1,
  className = '',
  itemType = 'media',
  onDragStart,
  onItemClick, // New prop for click handling
  onItemDoubleClick,
  onDeleteItem, // Callback to delete a media item (user uploads & AI generated)
  isSelectable = false, // New prop to enable selection mode
  renderCustomItem,
  endMessage,
  loadingComponent,
  errorComponent,
  dependencies = [],
  showDuration = true,
  showSize = false,
  showType = true,
  skipDeduplication = false, // Pass through to InfiniteScrollContainer
  // Justified layout props
  layout, // 'justified' for Google Photos-style layout
  targetRowHeight = 150,
  getAspectRatio,
  justifiedGap = 4,
}) => {
  // Track which audio items are playing
  const [playingAudioIds, setPlayingAudioIds] = useState(new Set());
  // Track intrinsic video aspect ratios detected from metadata
  const [videoAspectRatios, setVideoAspectRatios] = useState({});
  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || !onDeleteItem) return;
    setIsDeleting(true);
    try {
      await onDeleteItem(deleteTarget);
    } catch (error) {
      console.error('Failed to delete media:', error);
    } finally {
      setIsDeleting(false);
      setDeleteTarget(null);
    }
  };

  // Default drag handler - can be overridden via props
  const handleDragStart = (event, elementData) => {
    if (onDragStart) {
      onDragStart(event, elementData);
    } else {
      event.dataTransfer.setData(
        'application/json',
        JSON.stringify(elementData)
      );
      event.dataTransfer.effectAllowed = 'copy';
    }
  };

  // Handle hover events for video playback only (not audio)
  const handleMouseEnter = (event) => {
    const video = event.currentTarget.querySelector('video');

    if (video) {
      video.loop = true;
      video.play().catch(console.error);
    }
  };

  const handleMouseLeave = (event) => {
    const video = event.currentTarget.querySelector('video');

    if (video) {
      video.pause();
      video.currentTime = 0;
      video.load();
    }
  };

  // Handle click events for audio playback
  const handleAudioClick = (event, itemId) => {
    event.stopPropagation(); // Prevent drag from interfering
    const audioElement =
      event.currentTarget.parentElement.querySelector('audio');

    if (!audioElement) return;

    const isPlaying = playingAudioIds.has(itemId);

    if (isPlaying) {
      // Pause the audio
      audioElement.pause();
      audioElement.currentTime = 0;
      setPlayingAudioIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
    } else {
      // Stop all other audio elements first
      document.querySelectorAll('audio').forEach((audio) => {
        audio.pause();
        audio.currentTime = 0;
      });

      // Play this audio
      audioElement.loop = true;
      audioElement.play().catch(console.error);
      setPlayingAudioIds(new Set([itemId]));
    }
  };

  // Get appropriate icon based on type
  const getIcon = (type) => {
    switch (type) {
      case 'images':
      case 'image':
        return <ImageIcon size={26} className='text-gray-400' />;
      case 'audio':
        return <Music size={26} className='text-gray-400' />;
      case 'videos':
      case 'video':
        return <Video size={26} className='text-gray-400' />;
      case 'component':
      default:
        return <Puzzle size={26} className='text-gray-400' />;
    }
  };

  // Default render function for each media item
  const renderMediaItem = (item, index) => {
    // Check if this is a section header
    if (item.isHeader) {
      return (
        <div key={item.id} className='col-span-full mb-2 mt-4 first:mt-0'>
          <div className='flex items-center justify-between px-2'>
            <h3 className='text-sm font-semibold text-ag-green'>{item.title || item.name}</h3>
            <span className='text-xs text-gray-400'>{item.count} items</span>
          </div>
          <div className='h-px bg-ag-black-600 mt-2'></div>
        </div>
      );
    }

    // If custom render function is provided, use it
    if (renderCustomItem) {
      return renderCustomItem(
        item,
        index,
        handleDragStart,
        handleMouseEnter,
        handleMouseLeave
      );
    }

    const isVideo =
      item.type === 'video' ||
      item.type === 'videos' ||
      item.type === 'component';
    const isAudio = item.type === 'audio';
    const hasVideoUrl = isVideo && item.url;
    const hasAudioUrl = isAudio && item.url;
    const hasThumbnailOrPreview = item.thumbnail || item.preview;
    const itemId = item.id || `${itemType}-${index}`;
    const isAudioPlaying = playingAudioIds.has(itemId);

    return (
      <div
        key={itemId}
        draggable={!isSelectable}
        onDragStart={
          !isSelectable ? (e) => handleDragStart(e, item) : undefined
        }
        onClick={
          isSelectable && onItemClick ? () => onItemClick(item) : undefined
        }
        onDoubleClick={
          onItemDoubleClick ? () => onItemDoubleClick(item) : undefined
        }
        onMouseEnter={hasVideoUrl ? handleMouseEnter : undefined}
        onMouseLeave={hasVideoUrl ? handleMouseLeave : undefined}
        title={!item.isUserUpload && item.attribution ? item.attribution : undefined}
        className={`group bg-ag-black-600 rounded-lg p-3 hover:bg-ag-black-500 transition-all relative ${isSelectable
          ? 'cursor-pointer hover:scale-105 hover:ring-2 hover:ring-ag-green'
          : 'cursor-grab active:cursor-grabbing'
          } ${item.isUserUpload ? 'ring-1 ring-ag-green ring-opacity-30' : ''}`}
      >
        {/* Delete button - shown on hover for user uploads and AI generated */}
        {onDeleteItem && (item.isUserUpload || item.isAiGenerated) && (
          <button
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setDeleteTarget(item); }}
            onMouseDown={(e) => e.stopPropagation()}
            className='absolute top-1 left-1 z-20 p-1.5 rounded-full bg-black/60 text-gray-300 hover:bg-red-600 hover:text-white transition-all opacity-0 group-hover:opacity-100'
            title='Delete'
          >
            <Trash2 size={14} />
          </button>
        )}
        {/* User upload badge */}
        {item.isUserUpload && (
          <div className='absolute top-1 right-1 z-10'>
            <span className='px-1.5 py-0.5 text-xs bg-ag-green text-black rounded font-medium'>
              My Upload
            </span>
          </div>
        )}
        {/* AI Generated badge */}
        {item.isAiGenerated && !item.isUserUpload && (
          <div className='absolute top-1 right-1 z-10'>
            <span className='px-1.5 py-0.5 text-xs bg-ag-black-800 text-white rounded font-medium'>
              AI
            </span>
          </div>
        )}
        {/* CC License badge for Openverse content */}
        {!item.isUserUpload && item.provider === 'openverse' && item.license && (
          <div className='absolute top-1 right-1 z-10'>
            <a
              href={item.licenseUrl || item.attributionUrl}
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => e.stopPropagation()}
              className='px-1.5 py-0.5 text-xs bg-yellow-600 text-white rounded font-medium hover:bg-yellow-500 transition-colors flex items-center gap-1'
              title={`License: ${item.license?.toUpperCase()}${item.licenseVersion ? ` ${item.licenseVersion}` : ''}`}
            >
              <Scale size={10} />
              {item.license?.toUpperCase()}
            </a>
          </div>
        )}
        {/* Provider badge for other stock media */}
        {!item.isUserUpload && item.provider && item.provider !== 'openverse' && (
          <div className='absolute top-1 left-1 z-10'>
            <span className='px-1.5 py-0.5 text-xs bg-ag-black-800 bg-opacity-80 text-gray-300 rounded font-medium capitalize'>
              {item.provider}
            </span>
          </div>
        )}
        <div className='aspect-square bg-ag-black-600 rounded mb-2 flex items-center justify-center relative overflow-hidden'>
          {hasAudioUrl ? (
            <>
              <audio
                src={item.url}
                crossOrigin='anonymous'
                className='hidden'
                muted={false}
                preload='metadata'
              />
              {/* Background - either thumbnail or icon for audio */}
              {hasThumbnailOrPreview ? (
                <>
                  <img
                    crossOrigin='anonymous'
                    src={item.thumbnail || item.preview || ''}
                    alt={item.name}
                    className='w-full h-full object-cover rounded'
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className='hidden items-center justify-center w-full h-full'>
                    <Music size={40} className='text-gray-500' />
                  </div>
                </>
              ) : (
                <div className='flex items-center justify-center w-full h-full'>
                  <Music size={40} className='text-gray-500' />
                </div>
              )}
              {/* Play/Pause button overlay for audio */}
              <button
                onClick={(e) => handleAudioClick(e, itemId)}
                className='absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 transition-all group'
                onMouseDown={(e) => e.stopPropagation()} // Prevent drag on button click
              >
                <div className='bg-black bg-opacity-60 rounded-full p-3 group-hover:bg-opacity-80 transition-all'>
                  {isAudioPlaying ? (
                    <Pause size={24} className='text-white' />
                  ) : (
                    <Play size={24} className='text-white ml-0.5' />
                  )}
                </div>
              </button>
            </>
          ) : hasThumbnailOrPreview || hasVideoUrl ? (
            <>
              {hasVideoUrl ? (
                <video
                  crossOrigin='anonymous'
                  src={item.url || ''}
                  poster={item.preview || ''} // Start with preview as default
                  className='w-full h-full object-cover rounded'
                  muted
                  playsInline
                  preload='metadata'
                  onLoadStart={(e) => {
                    // Only try to load thumbnail if it exists
                    if (item.thumbnail) {
                      const img = new Image();
                      img.crossOrigin = 'anonymous';
                      img.onload = () => {
                        e.target.poster = item.thumbnail;
                      };
                      img.src = item.thumbnail;
                    }
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : (
                <img
                  crossOrigin='anonymous'
                  draggable='false'
                  src={item.thumbnail || item.preview || item.url || ''}
                  alt={item.name}
                  className='w-full h-full object-cover rounded'
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              )}
              <div className='hidden items-center justify-center w-full h-full absolute inset-0'>
                {getIcon(item.type)}
              </div>
            </>
          ) : (
            <div className='flex items-center justify-center w-full h-full'>
              {getIcon(item.type)}
            </div>
          )}
        </div>
        <h4 className='text-sm font-medium text-white mb-1 truncate'>
          {item.name || `${itemType} ${index + 1}`}
        </h4>
        {/* Attribution for stock media */}
        {!item.isUserUpload && item.attribution && (
          <div className='flex items-center gap-1 mb-1'>
            <p className='text-xs text-gray-400 truncate flex-1' title={item.attribution}>
              {item.attribution}
            </p>
            {/* Source link for CC content */}
            {item.sourceUrl && item.provider === 'openverse' && (
              <a
                href={item.sourceUrl}
                target='_blank'
                rel='noopener noreferrer'
                onClick={(e) => e.stopPropagation()}
                className='text-gray-400 hover:text-ag-green transition-colors flex-shrink-0'
                title='View source'
              >
                <ExternalLink size={12} />
              </a>
            )}
          </div>
        )}
        <div className='flex justify-between items-center'>
          {showSize && item.size && (
            <span className='text-xs text-gray-400'>{item.size}</span>
          )}
          {showDuration && item.duration && (
            <span className='text-xs text-gray-400'>{item.duration} secs</span>
          )}
          {showType && !item.size && !item.duration && (
            <span className='text-xs text-gray-400'>
              {item.type || itemType}
            </span>
          )}
        </div>
      </div>
    );
  };

  // Masonry/Justified overlay render function - images keep natural aspect ratio, no cropping
  const renderOverlayItem = (item, index) => {
    if (item.isHeader) {
      return (
        <div key={item.id} className='mb-2 mt-4 first:mt-0'>
          <div className='flex items-center justify-between px-2'>
            <h3 className='text-sm font-semibold text-ag-green'>{item.title || item.name}</h3>
            <span className='text-xs text-gray-400'>{item.count} items</span>
          </div>
          <div className='h-px bg-ag-black-600 mt-2'></div>
        </div>
      );
    }

    const isVideo =
      item.type === 'video' ||
      item.type === 'videos' ||
      item.type === 'component';
    const isAudio = item.type === 'audio';
    const hasVideoUrl = isVideo && item.url;
    const hasAudioUrl = isAudio && item.url;
    const hasThumbnailOrPreview = item.thumbnail || item.preview;
    const itemId = item.id || `${itemType}-${index}`;
    const isAudioPlaying = playingAudioIds.has(itemId);

    return (
      <div
        key={itemId}
        draggable={!isSelectable}
        onDragStart={!isSelectable ? (e) => handleDragStart(e, item) : undefined}
        onClick={isSelectable && onItemClick ? () => onItemClick(item) : undefined}
        onDoubleClick={onItemDoubleClick ? () => onItemDoubleClick(item) : undefined}
        onMouseEnter={hasVideoUrl ? handleMouseEnter : undefined}
        onMouseLeave={hasVideoUrl ? handleMouseLeave : undefined}
        title={!item.isUserUpload && item.attribution ? item.attribution : undefined}
        className={`group relative rounded-lg overflow-hidden bg-ag-black-800 ${
          isSelectable
            ? 'cursor-pointer hover:ring-2 hover:ring-ag-green'
            : 'cursor-grab active:cursor-grabbing'
        } ${item.isUserUpload ? 'ring-1 ring-ag-green ring-opacity-30' : ''}`}
      >
        {/* Delete button - shown on hover for user uploads and AI generated */}
        {onDeleteItem && (item.isUserUpload || item.isAiGenerated) && (
          <button
            onClick={(e) => { e.stopPropagation(); e.preventDefault(); setDeleteTarget(item); }}
            onMouseDown={(e) => e.stopPropagation()}
            className='absolute top-1 left-1 z-20 p-1 rounded-full bg-black/60 text-gray-300 hover:bg-red-600 hover:text-white transition-all opacity-0 group-hover:opacity-100'
            title='Delete'
          >
            <Trash2 size={12} />
          </button>
        )}
        {/* Badges */}
        {item.isUserUpload && (
          <div className='absolute top-1 right-1 z-10'>
            <span className='px-1 py-0.5 text-[10px] bg-ag-green text-black rounded font-medium'>
              My Upload
            </span>
          </div>
        )}
        {item.isAiGenerated && !item.isUserUpload && (
          <div className='absolute top-1 right-1 z-10'>
            <span className='px-1 py-0.5 text-[10px] bg-ag-black-800 text-white rounded font-medium'>
              AI
            </span>
          </div>
        )}
        {!item.isUserUpload && item.provider === 'openverse' && item.license && (
          <div className='absolute top-1 right-1 z-10'>
            <a
              href={item.licenseUrl || item.attributionUrl}
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => e.stopPropagation()}
              className='px-1 py-0.5 text-[10px] bg-yellow-600 text-white rounded font-medium hover:bg-yellow-500 transition-colors flex items-center gap-0.5'
              title={`License: ${item.license?.toUpperCase()}${item.licenseVersion ? ` ${item.licenseVersion}` : ''}`}
            >
              <Scale size={8} />
              {item.license?.toUpperCase()}
            </a>
          </div>
        )}
        {!item.isUserUpload && item.provider && item.provider !== 'openverse' && (
          <div className='absolute top-1 left-1 z-10'>
            <span className='px-1 py-0.5 text-[10px] bg-ag-black-800 bg-opacity-80 text-gray-300 rounded font-medium capitalize'>
              {item.provider}
            </span>
          </div>
        )}

        {/* Media - natural aspect ratio, no cropping */}
        {hasAudioUrl ? (
          <>
            <audio
              src={item.url}
              crossOrigin='anonymous'
              className='hidden'
              muted={false}
              preload='metadata'
            />
            <div className='flex items-center justify-center aspect-square'>
              <Music size={40} className='text-gray-500' />
            </div>
            {/* Play/Pause button overlay for audio */}
            <button
              onClick={(e) => handleAudioClick(e, itemId)}
              className='absolute inset-0 z-10 flex items-center justify-center bg-black bg-opacity-0 hover:bg-opacity-30 transition-all group/audio'
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className='bg-black bg-opacity-60 rounded-full p-3 group-hover/audio:bg-opacity-80 transition-all'>
                {isAudioPlaying ? (
                  <Pause size={24} className='text-white' />
                ) : (
                  <Play size={24} className='text-white ml-0.5' />
                )}
              </div>
            </button>
          </>
        ) : hasVideoUrl ? (
          <div
            className='relative w-full rounded-lg overflow-hidden'
            style={{ aspectRatio: videoAspectRatios[itemId] || (item.width && item.height ? item.width / item.height : undefined) }}
          >
            <video
              crossOrigin='anonymous'
              src={item.url}
              poster={(item.thumbnail && item.thumbnail !== item.url ? item.thumbnail : null) || item.preview || ''}
              className='absolute inset-0 w-full h-full object-cover rounded-lg'
              muted
              playsInline
              preload='metadata'
              onLoadedMetadata={(e) => {
                const v = e.target;
                if (v.videoWidth && v.videoHeight) {
                  setVideoAspectRatios((prev) => ({ ...prev, [itemId]: v.videoWidth / v.videoHeight }));
                }
              }}
            />
          </div>
        ) : hasThumbnailOrPreview ? (
          <img
            crossOrigin='anonymous'
            draggable='false'
            src={item.thumbnail || item.preview || item.url || ''}
            alt={item.name}
            className='w-full h-auto block rounded-lg group-hover:scale-[1.03] transition-transform duration-500'
            loading='lazy'
          />
        ) : (
          <div className='flex items-center justify-center aspect-square'>
            {getIcon(item.type)}
          </div>
        )}

        {/* Bottom gradient + name overlay */}
        <div className='absolute bottom-0 left-0 right-0 z-10 pointer-events-none'>
          <div className='bg-gradient-to-t from-black/80 to-transparent pt-6 px-2 pb-1.5'>
            <p className='text-[11px] font-medium text-white truncate'>
              {item.name || `${itemType} ${index + 1}`}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Default end message
  const defaultEndMessage = endMessage || (
    <div className='text-center py-4 text-gray-400 text-sm'>
      No more {itemType}s to load
    </div>
  );

  // Validate that we have a data fetcher
  if (!dataFetcher && !apiData) {
    console.error(
      'MediaRenderer: Either dataFetcher or apiData prop is required'
    );
    return (
      <div className='text-center py-4 text-red-400 text-sm'>
        Configuration error: No data source provided
      </div>
    );
  }

  // If apiData is provided but no dataFetcher, create a default API fetcher
  const effectiveDataFetcher =
    dataFetcher ||
    (async (page, pageSize) => {
      try {
        const response = await fetch(
          `${apiData}?page=${page}&pageSize=${pageSize}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        return {
          data: result.data || result.items || result,
          hasMore:
            result.hasMore !== undefined
              ? result.hasMore
              : result.data?.length === pageSize,
        };
      } catch (error) {
        console.error('Error fetching API data:', error);
        throw error;
      }
    });

  const useOverlayRenderer = layout === 'justified' || layout === 'masonry';

  return (
    <div className={`h-full flex flex-col ${className}`}>
      <div className='flex-1 min-h-0'>
        <InfiniteScrollContainer
          className='h-full p-2'
          dataFetcher={effectiveDataFetcher}
          renderItem={useOverlayRenderer ? renderOverlayItem : renderMediaItem}
          renderGrid={!useOverlayRenderer}
          gridCols={gridCols}
          pageSize={pageSize}
          endMessage={defaultEndMessage}
          loadingComponent={loadingComponent}
          errorComponent={errorComponent}
          dependencies={[apiData, ...dependencies]}
          skipDeduplication={skipDeduplication}
          layout={layout}
          targetRowHeight={targetRowHeight}
          getAspectRatio={getAspectRatio}
          justifiedGap={justifiedGap}
        />
      </div>

      {/* Delete confirmation modal */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Media"
        message={`Are you sure you want to delete "${deleteTarget?.name || 'this item'}"? This action cannot be undone.`}
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        type="danger"
      />
    </div>
  );
};

export default MediaRenderer;
